import type { DocumentActivityPageQuery } from '@/domains/activity/composables/use-document-activity/useDocumentActivity.types'
import type { ActivityDetailModel, ActivityPageModel } from '@/domains/activity/types/models/Activity.types'
import type { Bill } from '@/domains/bills/types/models/Bill.types.ts'
import type { BillPayment } from '@/domains/bills/types/models/BillPayment.types.ts'
import type { ConsignmentSettlement } from '@/domains/bills/types/models/Consignment.types.ts'
import type {
  BillActivitiesPayload,
  BillActivityPayload
} from '@/domains/bills/types/requests/BillActivityRequests.types'
import type {
  BillAttachmentDestroyPayload,
  BillAttachmentStorePayload
} from '@/domains/bills/types/requests/BillAttachmentRequests.types.ts'
import type {
  BillCancelPayload,
  BillDownloadPayload,
  BillGeneratePayload,
  BillIndexPayload,
  BillInventoryReceivePayload,
  BillMarkAsBilledPayload,
  BillSaveAsDraftPayload,
  BillUpdateDraftPayload,
  DraftBillFromPurchaseOrderPayload,
  DraftBillFromPurchaseOrderRequest
} from '@/domains/bills/types/requests/BillEndpointRequests.types.ts'
import type { ListBillLabelLinesPayload } from '@/domains/bills/types/requests/BillLabelLineRequests.types.ts'
import type {
  RecordBillPaymentPayload,
  VoidBillPaymentPayload
} from '@/domains/bills/types/requests/BillPaymentRequests.types.ts'
import type { BillRequest } from '@/domains/bills/types/requests/BillRequests.types.ts'
import type { ConsignmentSettlePayload } from '@/domains/bills/types/requests/ConsignmentRequests.types.ts'
import type { BillAttachmentStoreResponse } from '@/domains/bills/types/responses/BillAttachmentResponses.types.ts'
import type {
  DraftBillFromPurchaseOrderResponse,
  IndexBillResponse
} from '@/domains/bills/types/responses/BillResponses.types.ts'
import type { MediaResponse } from '@/domains/media/types/responses/MediaResponse.types.ts'
import type { LabelLineRow } from '@/domains/shelf-labels/types/models/LabelLineRow.types.ts'
import type { QueryParams } from '@/utils/url-util/urlUtil.ts'

import { twineServiceClient } from '@/services/api'
import { relativeZiggyRoute } from '@/utils/url-util/urlUtil.ts'

const callBills = () => {
  const index = (payload: BillIndexPayload) => {
    return twineServiceClient.get<IndexBillResponse, BillIndexPayload>({
      endpoint: relativeZiggyRoute('admin.finance.bills.index'),
      queryParams: {
        ...payload.queryParams
      } as QueryParams<BillIndexPayload>,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  const saveAsDraft = (payload: BillSaveAsDraftPayload) => {
    return twineServiceClient.post<Bill, BillRequest>({
      endpoint: relativeZiggyRoute('admin.finance.bills.store'),
      body: payload,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  const updateDraft = (payload: BillUpdateDraftPayload) => {
    return twineServiceClient.put<Bill, BillRequest>({
      endpoint: relativeZiggyRoute('admin.finance.bills.update', { bill: payload.id }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  const cancelDraft = (payload: BillCancelPayload) => {
    return twineServiceClient.post<Bill, BillRequest>({
      endpoint: relativeZiggyRoute('admin.finance.bills.cancel', { bill: payload.id }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  const markAsBilled = (payload: BillMarkAsBilledPayload & { preventConcurrent?: boolean; token?: string }) => {
    return twineServiceClient.post<Bill, BillRequest>({
      endpoint: relativeZiggyRoute('admin.finance.bills.bill', { bill: payload.id }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Records a payment to the supplier against a regular bill (COR-46).
   *
   * ⚠ The caller mints `idempotency_key` once per opening of the form. The backend answers a second request
   * carrying it with the payment the first made, so a double-tapped button records one payment.
   */
  const recordPayment = (payload: RecordBillPaymentPayload) => {
    return twineServiceClient.post<BillPayment, RecordBillPaymentPayload['body']>({
      endpoint: relativeZiggyRoute('admin.finance.bills.payments.store', { bill: payload.billId }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /** Voids a payment made in error. The reason is required; the payment stays on record. */
  const voidPayment = (payload: VoidBillPaymentPayload) => {
    return twineServiceClient.post<BillPayment, VoidBillPaymentPayload['body']>({
      endpoint: relativeZiggyRoute('admin.finance.bills.payments.void', {
        bill: payload.billId,
        payment: payload.paymentId
      }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  const inventoryReceive = (payload: BillInventoryReceivePayload) => {
    return twineServiceClient.post<{ message: string }, BillInventoryReceivePayload['body']>({
      endpoint: relativeZiggyRoute('admin.finance.bills.inventory-receives', { bill: payload.billId }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Uploads invoice attachments to a saved bill. The files travel as a
   * `media[]` FormData body, which `twineServiceClient` sends as
   * `multipart/form-data`. On a 422 the thrown error carries Laravel's
   * `{ message, errors }` payload under `.data`, so callers can reuse
   * `extractApiErrors` / `getFieldErrors` (error keys are `media.0`,
   * `media.1`, ...).
   */
  const storeAttachment = async (payload: BillAttachmentStorePayload): Promise<BillAttachmentStoreResponse> => {
    const formData = new FormData()
    payload.files.forEach(file => formData.append('media[]', file))

    const { data } = await twineServiceClient.post<BillAttachmentStoreResponse, FormData>({
      endpoint: relativeZiggyRoute('admin.finance.bills.attachments.store', { bill: payload.billId }),
      body: formData,
      preventConcurrent: false
    })

    return data.value as BillAttachmentStoreResponse
  }

  /**
   * Generate the PDF for an issued (Billed/Paid) bill.
   *
   * POST /admin/finance/bills/{bill}/generate
   */
  const generate = (payload: BillGeneratePayload) => {
    return twineServiceClient.post<MediaResponse>({
      endpoint: relativeZiggyRoute('admin.finance.bills.generate', { bill: payload.id }),
      body: {},
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Download a previously generated bill PDF.
   *
   * GET /admin/finance/bills/{bill}/download
   */
  const download = (payload: BillDownloadPayload) => {
    return twineServiceClient.get<MediaResponse>({
      endpoint: relativeZiggyRoute('admin.finance.bills.download', { bill: payload.id }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Drafts a bill from an issued purchase order and answers its id.
   *
   * The bill is built entirely on the backend: `BillValidator` recomputes every total with
   * `BillTotalCalculator` and refuses a mismatch, so numbers assembled on the client could only
   * ever fail the check. The dialog sends the merchant's three answers and nothing else.
   *
   * `preventConcurrent` stays at its default `true` — the token derives from the endpoint and
   * method, so a double-pressed button aborts its own first request rather than raising two bills.
   *
   * Rejects with an `Error` carrying the backend's `message` on a 422. That covers a purchase order
   * that is not issued, one that already holds a live bill, and a basis that came to no quantity.
   */
  const draftFromPurchaseOrder = (payload: DraftBillFromPurchaseOrderPayload) => {
    return twineServiceClient.post<DraftBillFromPurchaseOrderResponse, DraftBillFromPurchaseOrderRequest>({
      endpoint: relativeZiggyRoute('admin.inventory.purchase-orders.bill', {
        purchaseOrder: payload.purchaseOrderId
      }),
      body: payload.body,
      token: payload.token
    })
  }

  /** Deletes a single attachment (endpoint returns 204). */
  const deleteAttachment = async (payload: BillAttachmentDestroyPayload): Promise<void> => {
    await twineServiceClient.delete({
      endpoint: relativeZiggyRoute('admin.finance.bills.attachments.destroy', {
        bill: payload.billId,
        media: payload.mediaUuid
      }),
      preventConcurrent: false
    })
  }

  /**
   * Pays a consignment supplier for the units that have sold.
   *
   * ⚠ `idempotency_key` is minted by the caller and is not optional. It is what makes a
   * double-tapped button one payment instead of two: the backend answers the second request with
   * the payment the first made, rather than posting another.
   *
   * ⚠ `paid_at` must be ISO-8601 — pass `date.toISOString()`. A `YYYY-MM-DD HH:mm:ss` string fails
   * inside the backend's date cast rather than as a validation error, so the caller gets a 500 and
   * no clue why. `toISOString()`'s milliseconds are accepted: `config/data.php` lists a second
   * format for them, because DATE_ATOM alone rejects the only ISO string a browser can produce.
   */
  const settleConsignment = (payload: ConsignmentSettlePayload) => {
    return twineServiceClient.post<ConsignmentSettlement, ConsignmentSettlePayload['body']>({
      endpoint: relativeZiggyRoute('admin.finance.bills.consignment-settlements.store', {
        bill: payload.billId
      }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /** The label rows for a bill's received lines. The tenant's default template answers when none is named. */
  const listBillLabelLines = (payload: ListBillLabelLinesPayload) => {
    return twineServiceClient.get<LabelLineRow[]>({
      endpoint: relativeZiggyRoute('admin.finance.bills.label-lines.index', { bill: payload.billId }),
      queryParams: { template: payload.template },
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * A page of the bill's history, newest first (DEC-2026-094)
   * GET /admin/finance/bills/{bill}/activities?cursor=&category=
   */
  const listActivities = (payload: BillActivitiesPayload) => {
    return twineServiceClient.get<ActivityPageModel, DocumentActivityPageQuery>({
      endpoint: relativeZiggyRoute('admin.finance.bills.activities.index', { bill: payload.billId }),
      queryParams: payload.queryParams as QueryParams<DocumentActivityPageQuery>,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * What one history row opens to, fetched only when a person expands it
   * GET /admin/finance/bills/{bill}/activities/{activity}
   */
  const showActivity = (payload: BillActivityPayload) => {
    return twineServiceClient.get<ActivityDetailModel>({
      endpoint: relativeZiggyRoute('admin.finance.bills.activities.show', {
        bill: payload.billId,
        activity: payload.activityId
      }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  return {
    index,
    listActivities,
    showActivity,
    settleConsignment,
    listBillLabelLines,
    saveAsDraft,
    updateDraft,
    markAsBilled,
    recordPayment,
    voidPayment,
    cancelDraft,
    inventoryReceive,
    generate,
    download,
    draftFromPurchaseOrder,
    storeAttachment,
    deleteAttachment
  }
}

export { callBills }
